"use client"
import { useEffect, useRef, useState } from "react"
import { useHudDates } from "../../shared/playbookHUDInfra"
import { getYearHeadings } from "@/app/common/blocks/perspective-choice/yearHeadings"

// The year table of contents the HUD's current-year pill toggles: every
// "<year>: <title>" section heading of the selected reading path, in reading
// order. Selecting one navigates via the URL hash, so useAnchorDeepLink does
// the branch expansion + stabilized scroll exactly like any other deep link.
//
// Rendered by HudPersistentGlobeAndGraph in a zero-height layer pinned to the
// graph's box and stacked above the globe, so the menu's coordinates are
// graph-local and it paints over the globe/graph instead of being trapped
// under them (same trick as the above-globe band overlay layer).

// Menu top-left, in graph-local coordinates: under the current-year pill
// (pinned at left 14 / top 12, ~32px tall — see the CURRENT_YEAR_PILL_*
// consts in ExtrapolatedGraphRenderer) with a small gap.
const MENU_LEFT_PX = 14
const MENU_TOP_PX = 52

export const YearJumpMenu = ({ onClose }: { onClose: () => void }) => {
  // Scanned once per open (the menu mounts on open, unmounts on close): the
  // path's year sections can't change while the menu is up.
  const [headings] = useState(getYearHeadings)
  const { targetDate } = useHudDates()
  const rootRef = useRef<HTMLDivElement | null>(null)

  // The section the reader is in: the last heading at or before the HUD year.
  const currentYear = targetDate.getFullYear()
  const activeId = headings.reduce<string | null>(
    (active, heading) => (heading.year <= currentYear ? heading.id : active),
    null,
  )

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (rootRef.current?.contains(target)) return
      // The pill's own click toggles the menu — closing on its pointerdown
      // here would make that click immediately reopen what it just closed.
      if (target.closest("[data-hud-year-pill]")) return
      onClose()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("pointerdown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [onClose])

  const jumpToHeading = (id: string) => {
    if (window.location.hash === `#${id}`) {
      // Re-assigning the current hash fires no hashchange event — re-dispatch
      // so useAnchorDeepLink re-runs the expand-and-scroll anyway.
      window.dispatchEvent(new HashChangeEvent("hashchange"))
    } else {
      window.location.hash = id
    }
    onClose()
  }

  if (headings.length === 0) return null

  return (
    <div
      ref={rootRef}
      role="menu"
      aria-label="Jump to a year"
      className="pointer-events-auto absolute z-[3] max-h-[480px] w-max max-w-[calc(100%-28px)] overflow-y-auto rounded-md border-[1.5px] border-current bg-[#fffff8] p-1 font-mono text-[13px] shadow-[0_10px_30px_rgba(0,0,0,0.16)]"
      style={{ left: MENU_LEFT_PX, top: MENU_TOP_PX }}
    >
      {headings.map(heading => (
        <button
          key={heading.id}
          type="button"
          role="menuitem"
          className={`flex w-full items-center overflow-hidden whitespace-nowrap rounded px-2 py-1.5 text-left leading-snug hover:font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-black ${
            heading.id === activeId
              ? "font-bold text-[#8b0000]"
              : "text-black"
          }`}
          onClick={() => jumpToHeading(heading.id)}
        >
          <span className="font-bold">{heading.year}</span>
          {heading.after && (
            <span className="truncate">: {heading.after}</span>
          )}
        </button>
      ))}
    </div>
  )
}
